import { isNavigationMenuOpenState } from '@modules/application/atoms';
import { Business, DocumentScanner, People, TaskAlt, Timeline, ViewList, Web } from '@mui/icons-material';
import Label from '@mui/icons-material/Label';
import { Box, Drawer, Toolbar, styled } from '@mui/material';
import { getTenantIdentifier } from '@utils/getTenantIdentifier';
import { useGetMe } from '@utils/useGetMe';
import { useFeatureFlagsQuery } from 'gql/index';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { expandedMenuWidth } from '.';
import { NavigationMenuLink } from './NavigationMenuLink';
import { NavigationMenuSubMenu } from './NavigationMenuSubMenu';

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
    }),
    overflowX: 'hidden',
    width: `${expandedMenuWidth}px`,
    borderRight: 'none',
    boxShadow: 'none',
    boxSizing: 'border-box',
  },
  '& .collapsed h6': {
    visibility: 'hidden'
  },
  '& nav': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    paddingTop: theme.spacing(2)
  }
}));

export const NavigationMenu: React.FC = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();

  const [isNavigationMenuOpen, setIsNavigationMenuOpen] = useAtom(isNavigationMenuOpenState);
  const { data: featureFlags } = useFeatureFlagsQuery({}, { select: p => p.featureFlags, staleTime: 300000 });
  const tenantIdentifier = getTenantIdentifier();
  const prefixWithTenant = (path: string) => `/${tenantIdentifier}/${path}`;

  useEffect(() => {
    setIsNavigationMenuOpen(false);
  }, [location, setIsNavigationMenuOpen]);

  const { hasTenantAdminRole, hasMultiTenantAdminRole } = useGetMe();

  return (
    <StyledDrawer
      open={isNavigationMenuOpen}
      onClose={() => setIsNavigationMenuOpen(false)}
      anchor="left"
    >
      <Toolbar sx={{ marginBottom: 1 }} />
      <Box component="nav">
        <NavigationMenuSubMenu
          icon={<Web htmlColor='inherit' />}
          title={formatMessage({ id: 'Projects' })}
          expanded={location.pathname.startsWith(prefixWithTenant('projects'))}
        >
          <NavigationMenuLink to={prefixWithTenant('projects')} end title={formatMessage({ id: 'Projects' })} sx={{ pl: 4 }} />
          <NavigationMenuLink to={prefixWithTenant('projects/templates')} title={formatMessage({ id: 'Project templates' })} sx={{ pl: 4 }} />
        </NavigationMenuSubMenu>
        <NavigationMenuLink icon={<TaskAlt htmlColor='inherit' />} title={formatMessage({ id: 'Tasks' })} to={prefixWithTenant('tasks')} />
        <NavigationMenuLink icon={<ViewList htmlColor='inherit' />} title={formatMessage({ id: 'Form templates' })} to={prefixWithTenant('templates')} />
        <NavigationMenuLink icon={<Timeline color='inherit' />} title={formatMessage({ id: 'Workflow templates' })} to={prefixWithTenant('workflows')} />

        {featureFlags?.documentTemplatesEnabled && (
          <NavigationMenuLink icon={<DocumentScanner htmlColor='inherit' />} title={formatMessage({ id: 'Document templates' })} to={prefixWithTenant('document-templates')} />
        )}

        {hasTenantAdminRole && <>
          <NavigationMenuLink icon={<People htmlColor='inherit' />} title={formatMessage({ id: 'Internal users' })} to={prefixWithTenant('users')} />
          <NavigationMenuLink icon={<Label htmlColor='inherit' />} title={formatMessage({ id: 'Branding' })} to={prefixWithTenant('branding')} />
        </>}
        {hasMultiTenantAdminRole && (
          <NavigationMenuLink icon={<Business htmlColor='inherit' />} title={formatMessage({ id: 'Tenants' })} to={prefixWithTenant('tenants')} />
        )}
      </Box>
    </StyledDrawer>
  );
};